import { useEffect, useRef } from 'react';
import { type CandleResolution } from '../components/charts/convergence';
import { type MarketSeriesData } from '../api//marketPricesClient';

interface UseChartAutoZoomParams {
    tradeMarketId: string | null;
    probSeries: MarketSeriesData[];
    probLoading: boolean;
    probStartDate: Date | null;
    probEndDate: Date | null;
    userSetTimeRange: React.MutableRefObject<boolean>;
    userSetResolution: React.MutableRefObject<boolean>;
    setTimeRange: (range: [Date, Date] | null) => void;
    setResolution: (res: CandleResolution | null) => void;
}

export function useChartAutoZoom({
    tradeMarketId,
    probSeries,
    probLoading,
    probStartDate,
    probEndDate,
    userSetTimeRange,
    userSetResolution,
    setTimeRange,
    setResolution,
}: UseChartAutoZoomParams) {
    const prevAutoZoomMarketId = useRef<string | null>(null);

    useEffect(() => {
        if (!tradeMarketId) {
            if (
                prevAutoZoomMarketId.current !== null &&
                !userSetTimeRange.current
            ) {
                setTimeRange(null);
                setResolution(null);
                userSetResolution.current = false;
            }
            prevAutoZoomMarketId.current = null;
            return;
        }

        if (userSetTimeRange.current) {
            prevAutoZoomMarketId.current = tradeMarketId;
            return;
        }

        if (prevAutoZoomMarketId.current === tradeMarketId) return;
        if (probLoading || probSeries.length === 0) return;

        const series = probSeries.find(s => s.conditionId === tradeMarketId);
        if (!series || series.data.length === 0) {
            prevAutoZoomMarketId.current = tradeMarketId;
            return;
        }

        const firstActiveCandle = series.data.find(d => d.vol > 0);
        if (!firstActiveCandle) {
            prevAutoZoomMarketId.current = tradeMarketId;
            return;
        }

        const firstActiveDate = new Date(firstActiveCandle.t);
        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        const isLateStart =
            !probStartDate ||
            firstActiveDate.getTime() - probStartDate.getTime() > ONE_DAY_MS;

        if (isLateStart) {
            const now = new Date();
            const rangeEnd =
                probEndDate && probEndDate < now ? probEndDate : now;
            const paddingMs = Math.max(
                (rangeEnd.getTime() - firstActiveDate.getTime()) * 0.02,
                60 * 1000
            );
            const paddedStart = new Date(firstActiveDate.getTime() - paddingMs);
            const newRange: [Date, Date] = [paddedStart, rangeEnd];
            setTimeRange(newRange);
            if (!userSetResolution.current) setResolution(null);
        }

        prevAutoZoomMarketId.current = tradeMarketId;
    }, [tradeMarketId, probSeries, probLoading, probStartDate, probEndDate]);

    return { prevAutoZoomMarketId };
}
